/**
 * /admin/* catch-all placeholder — renders for any admin sub-route
 * advertised in `ADMIN_SECTIONS` that doesn't have a more-specific
 * route registered in `App.tsx`.
 *
 * Without this catch-all, clicking a not-yet-built section's link on
 * `AdminPage` would miss the `/admin` route group and fall through to
 * the workspace fallback redirect — a confusing UX. The placeholder
 * keeps the user inside the admin shell and surfaces the section's
 * description + the `todo` pointer (issue / wave / follow-up) so the
 * operator knows what's coming.
 *
 * Rendering behavior: the TODO box renders whenever the matched
 * section has a `todo` field, regardless of `status`. In practice
 * this only fires for `status: 'unbuilt'` sections — shipped sections
 * have specific route entries ABOVE this catch-all in `App.tsx`, so
 * React Router resolves them first and the placeholder is never
 * reached. If a shipped section ever loses its route entry, the
 * placeholder still degrades gracefully (the operator sees the
 * section's description + the stale `todo` if one is set).
 *
 * When a section ships its real screen, register its route ABOVE
 * this catch-all in `App.tsx` and flip the section's `status` to
 * `'shipped'` in `sections.ts`. No change to this file needed.
 */
import { Link, useLocation } from 'react-router-dom'
import { findAdminSectionByPath } from './sections.js'

export function AdminPlaceholder() {
  const location = useLocation()
  const section = findAdminSectionByPath(location.pathname)
  // Only the parent-shape entries carry `todo` in the union; child
  // entries inherit their parent's lifecycle implicitly. The TODO
  // tracker line renders whenever the matched entry has a `todo`
  // field (regardless of `status`) — see the file header for why
  // shipped sections never reach this code path in practice.
  const todo = section && 'todo' in section ? section.todo : undefined
  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">
          {section?.label ?? 'Admin section'}
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          This admin section is not yet built.
        </p>
      </div>

      {section ? (
        <div className="border rounded-md p-4 space-y-2">
          <p className="text-sm">{section.description}</p>
          {todo && (
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground">
              TODO: {todo}
            </p>
          )}
        </div>
      ) : (
        <div className="border rounded-md p-4 space-y-2">
          <p className="text-sm">
            <code className="text-xs">{location.pathname}</code> is not a
            recognized admin route.
          </p>
        </div>
      )}

      <p>
        <Link to="/admin" className="text-sm font-medium hover:underline">
          ← Back to Admin
        </Link>
      </p>
    </div>
  )
}
